/**
 * OnbDone — onboarding step 4 (account created).
 *
 * A confirmation mark, the headline, and the one thing worth doing next. The
 * single CTA opens the assistant (→ /chat) — the captured-renovation "project"
 * home was removed 2026-07-06.
 *
 * 2026-08: the celebratory "Готово!" is replaced by a statement of WHAT the
 * user now has and what to do with it. The tip — upload your contractor's
 * estimate — is the highest-value first action in the entire product, so it is
 * a ruled next-step row with its own weight, not an accent-tinted card floating
 * under the headline. The success mark keeps the semantic success token; it is
 * an outline, not a filled disc, because a filled green circle plus a headline
 * plus a card is the stock "you're all set" template.
 */
import { type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { Button, Icon } from "@garden/ui";

import { ONBOARDING_NS } from "@/features/onboarding/api";

export interface OnbDoneProps {
  /** Open the assistant (`/chat`). The primary (only) action. */
  onOpenAssistant: () => void;
}

export function OnbDone({ onOpenAssistant }: OnbDoneProps): ReactElement {
  const { t } = useTranslation(ONBOARDING_NS);
  return (
    <div className="flex flex-1 flex-col" data-testid="onb-done">
      <div className="flex flex-1 flex-col items-center justify-center py-4 text-center">
        <div
          className="mb-6 grid h-[76px] w-[76px] place-items-center rounded-full border-2 border-[var(--g-success)] text-[var(--g-success)]"
          aria-hidden="true"
        >
          <Icon name="check" size={38} stroke={2.4} />
        </div>
        <h1 className="font-display m-0 text-[28px] font-extrabold leading-[1.08] text-foreground">
          {t("done.h1")}
        </h1>
        <p className="mx-auto mt-3 max-w-[36ch] text-[15px] leading-[1.5] text-[var(--g-ink-2)]">
          {t("done.lede")}
        </p>

        {/* The next step, as a ruled row — the estimate upload is the single
            highest-value thing a new user can do, so it gets stated plainly. */}
        <div className="mt-7 w-full overflow-hidden rounded-[var(--g-radius-lg)] border border-[var(--g-rule)] bg-[var(--g-surface)] text-left">
          <div className="flex items-start gap-3 px-4 py-3.5">
            <span className="grid size-9 shrink-0 place-items-center rounded-[var(--g-radius)] bg-[var(--g-accent-soft)] text-[var(--g-accent)]">
              <Icon name="clip" size={18} />
            </span>
            <span className="min-w-0">
              <span className="block text-[14px] font-semibold leading-snug text-foreground">
                {t("done.tipTitle")}
              </span>
              <span className="mt-0.5 block text-[13px] leading-snug text-[var(--g-ink-2)]">
                {t("done.tip")}
              </span>
            </span>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-2.5 pt-6">
        <Button
          size="lg"
          full
          leading="sparkles"
          data-testid="onb-done-open-assistant"
          onClick={onOpenAssistant}
        >
          {t("cta.openAssistant")}
        </Button>
      </div>
    </div>
  );
}

export default OnbDone;
